import { useCallback, useRef, useState } from "react";
import { useToast } from "./toast";

export interface UseAsyncActionOptions<T> {
  /** Toast shown while the promise is pending. Pass `null` to disable. */
  loadingMessage?: string | null;
  /** Toast shown on success. Pass `null` to disable. */
  successMessage?: string | null;
  /** Description under the success toast. */
  successDescription?: string;
  /** Override auto-generated error toast text. */
  errorMessage?: (err: Error) => string;
  /** Called on success. */
  onSuccess?: (value: T) => void;
  /** Called on error. */
  onError?: (err: Error) => void;
}

/**
 * Wraps an async handler with pending state and unified toast feedback.
 * Prevents the handler from running concurrently with itself.
 */
export function useAsyncAction<Args extends unknown[], T>(
  fn: (...args: Args) => Promise<T>,
  options: UseAsyncActionOptions<T> = {}
) {
  const toast = useToast();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const pendingRef = useRef(false);

  const run = useCallback(
    async (...args: Args): Promise<T | undefined> => {
      if (pendingRef.current) return undefined;
      pendingRef.current = true;
      setPending(true);
      setError(null);

      let loadingId: string | null = null;
      if (options.loadingMessage) {
        loadingId = toast.loading(options.loadingMessage);
      }

      try {
        const result = await fn(...args);
        if (loadingId) toast.dismiss(loadingId);
        if (options.successMessage !== null && options.successMessage !== undefined) {
          toast.success(options.successMessage, options.successDescription);
        }
        options.onSuccess?.(result);
        return result;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        if (loadingId) toast.dismiss(loadingId);
        const msg = options.errorMessage ? options.errorMessage(err) : err.message;
        toast.error("Something went wrong", msg);
        options.onError?.(err);
        return undefined;
      } finally {
        pendingRef.current = false;
        setPending(false);
      }
    },
    [fn, options, toast]
  );

  return { run, pending, error };
}
